import React, { useCallback, useEffect, useState } from 'react';
import { App, Button, Modal, Select, Table, Tag, Tooltip, Typography } from 'antd';
import apiService, { ServiceType } from '../../services/api';
import { useIsMobile } from '../../hooks/useIsMobile';

const core = apiService.getService(ServiceType.CORE);

type AccountService = 'API_DEFAULT';
type OrderService = 'FIX';

export interface SetupRow {
  id: string;
  name: string | null;
  externalPortfolioId?: string;
  accountType?: string;
  brokerMode?: 'SERVER' | 'CLIENT';
  accountService: AccountService | null;
  orderService: OrderService | null;
  /** BE: why the account may not be sent from Fortuna, null when it may. */
  delegationBlockedReason: string | null;
}

interface Options {
  accountServices: AccountService[];
  orderServices: OrderService[];
  delegable: { accountService: AccountService; orderService: OrderService };
}

export const SERVICE_LABEL: Record<string, string> = { API_DEFAULT: 'Broker API (default)', FIX: 'FIX' };

const errText = (e: unknown): string => {
  const data = (e as { response?: { data?: { message?: unknown } } })?.response?.data;
  const msg = data?.message;
  if (Array.isArray(msg)) return msg.join(', ');
  if (typeof msg === 'string') return msg;
  return e instanceof Error ? e.message : String(e);
};

export const isSetUp = (r: SetupRow): boolean => !!r.accountService && !!r.orderService;

/** The portfolios with their setup, and the choices the setup screen offers. */
export function useAccountSetup() {
  const [rows, setRows] = useState<SetupRow[] | null>(null);
  const [options, setOptions] = useState<Options | null>(null);
  const load = useCallback(async () => {
    const [r, o] = await Promise.all([
      core.get<SetupRow[]>('/portfolios'),
      core.get<Options>('/portfolios/services/options'),
    ]);
    setRows(Array.isArray(r.data) ? r.data : []);
    setOptions(o.data);
  }, []);
  return { rows, options, load };
}

/**
 * One row per account: where its money is read from, how its orders go out, and — for an
 * account the terminal sends — the switch to Fortuna, open only for the delegable setup.
 */
export const AccountSetupTable: React.FC<{ rows: SetupRow[]; options: Options; onChanged: () => void }> = ({ rows, options, onChanged }) => {
  const { message, modal } = App.useApp();
  const isMobile = useIsMobile();
  const [busy, setBusy] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, Partial<Pick<SetupRow, 'accountService' | 'orderService'>>>>({});

  const pick = (r: SetupRow, kind: 'accountService' | 'orderService') => draft[r.id]?.[kind] ?? r[kind] ?? undefined;
  const changed = (r: SetupRow) =>
    pick(r, 'accountService') !== (r.accountService ?? undefined) || pick(r, 'orderService') !== (r.orderService ?? undefined);

  const save = async (r: SetupRow) => {
    setBusy(r.id);
    try {
      await core.patch(`/portfolios/${r.id}/services`, {
        accountService: pick(r, 'accountService'),
        orderService: pick(r, 'orderService'),
      });
      message.success(`${r.name ?? r.id} set up`);
      setDraft((d) => { const { [r.id]: _, ...rest } = d; return rest; });
      onChanged();
    } catch (e) {
      message.error(errText(e));
    } finally {
      setBusy(null);
    }
  };

  const delegate = (r: SetupRow) => modal.confirm({
    title: `Send ${r.name ?? r.id} from Fortuna?`,
    content: 'From the next bot start, Fortuna sends this account\'s orders over FIX and reads its money from the broker API. The desktop terminal no longer needs to run for it.',
    okText: 'Send from Fortuna',
    onOk: async () => {
      try {
        await core.patch(`/portfolios/${r.id}/broker-mode`, { mode: 'SERVER' });
        message.success(`${r.name ?? r.id} is sent from Fortuna`);
        onChanged();
      } catch (e) {
        message.error(errText(e));
      }
    },
  });

  const serviceSelect = (r: SetupRow, kind: 'accountService' | 'orderService', list: string[]) => (
    <Select
      size={isMobile ? 'middle' : 'small'}
      style={isMobile ? { width: '100%' } : { minWidth: 170 }}
      placeholder="Not set up"
      value={pick(r, kind)}
      options={list.map((v) => ({ value: v, label: SERVICE_LABEL[v] ?? v }))}
      onChange={(v) => setDraft((d) => ({ ...d, [r.id]: { ...d[r.id], [kind]: v } }))}
      disabled={busy === r.id}
    />
  );

  const action = (r: SetupRow) => {
    const ready = !!pick(r, 'accountService') && !!pick(r, 'orderService');
    const btnSize = isMobile ? 'middle' : 'small';
    if (changed(r) || !isSetUp(r)) {
      return (
        <Button size={btnSize} block={isMobile} type="primary" disabled={!ready} loading={busy === r.id} onClick={() => save(r)}>
          Save
        </Button>
      );
    }
    if (r.brokerMode !== 'CLIENT') return null;
    return (
      <Tooltip title={r.delegationBlockedReason ?? ''}>
        <Button size={btnSize} block={isMobile} disabled={!!r.delegationBlockedReason} onClick={() => delegate(r)}>
          Send from Fortuna
        </Button>
      </Tooltip>
    );
  };

  const ordersSentBy = (r: SetupRow) =>
    r.brokerMode === 'CLIENT' ? <Tag color="gold">Desktop terminal</Tag> : <Tag color="blue">Fortuna</Tag>;

  // Five columns do not fit a phone, and the gate cannot be closed — a clipped Save would
  // leave the user stuck. One card per account instead, fields stacked.
  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((r) => (
          <div key={r.id} data-testid={`account-setup-card-${r.id}`} style={{ border: '1px solid #2B3139', borderRadius: 8, padding: 12, background: '#1E2329' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: '#EAECEF', fontWeight: 600, wordBreak: 'break-word' }}>{r.name ?? r.id}</div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>{r.accountType}</Typography.Text>
              </div>
              {ordersSentBy(r)}
            </div>
            <div style={{ fontSize: 12, color: '#848E9C', marginBottom: 4 }}>Account service</div>
            {serviceSelect(r, 'accountService', options.accountServices)}
            <div style={{ fontSize: 12, color: '#848E9C', margin: '10px 0 4px' }}>Order service</div>
            {serviceSelect(r, 'orderService', options.orderServices)}
            {action(r) && <div style={{ marginTop: 12 }}>{action(r)}</div>}
          </div>
        ))}
      </div>
    );
  }

  return (
    <Table<SetupRow>
      size="small"
      rowKey="id"
      pagination={false}
      dataSource={rows}
      columns={[
        {
          title: 'Account',
          render: (_, r) => (
            <>
              <div>{r.name ?? r.id}</div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>{r.accountType}</Typography.Text>
            </>
          ),
        },
        {
          title: 'Orders sent by',
          render: (_, r) => ordersSentBy(r),
        },
        { title: 'Account service', render: (_, r) => serviceSelect(r, 'accountService', options.accountServices) },
        { title: 'Order service', render: (_, r) => serviceSelect(r, 'orderService', options.orderServices) },
        {
          title: '',
          align: 'right',
          render: (_, r) => action(r),
        },
      ]}
    />
  );
};

/** Mandatory: while any account has no setup, this screen stays open and cannot be dismissed. Once
 *  saved, the setup lives on the account (BE) and the screen never shows for it again. */
export const AccountSetupGate: React.FC = () => {
  const { rows, options, load } = useAccountSetup();

  useEffect(() => {
    if (!localStorage.getItem('access_token')) return;
    load().catch(() => { /* unreachable BE: nothing to ask about yet */ });
  }, [load]);

  const todo = (rows ?? []).filter((r) => !isSetUp(r));
  if (!options || todo.length === 0) return null;
  return (
    <Modal open title="Set up your accounts" closable={false} maskClosable={false} keyboard={false} footer={null} width={760}>
      <Typography.Paragraph type="secondary">
        Choose where each account&apos;s money is read from and how its orders go out. Every account must be set
        up before you continue. Only an account set up
        with {SERVICE_LABEL[options.delegable.accountService]} + {SERVICE_LABEL[options.delegable.orderService]} can
        be sent from Fortuna instead of the desktop terminal.
      </Typography.Paragraph>
      <AccountSetupTable rows={todo} options={options} onChanged={() => { void load(); }} />
    </Modal>
  );
};

/** The same table as a page section, for every account (Portfolio → Setup). */
export const AccountSetupSection: React.FC<{ active: boolean }> = ({ active }) => {
  const { message } = App.useApp();
  const { rows, options, load } = useAccountSetup();
  useEffect(() => {
    if (active) load().catch((e) => message.error(errText(e)));
  }, [active, load, message]);
  if (!rows || !options) return null;
  return <AccountSetupTable rows={rows} options={options} onChanged={() => { void load(); }} />;
};
